import type { TranslateResult } from 'vue-i18n';
import type { ListMutationTitleDetailParam } from '@/helpers/composables/useListActions';

import { ObjectType } from '@/@types/graphql-types';

import ToastModal from '@/components/modals/vfm/ToastModal.vue';
import { TitleListName, TitleListNames } from '@/interfaces/responses/title-list';
import {
	ShowToastPayload,
	ToastAction,
	ToastButton,
	ToastCallback,
	ToastColors,
	ToastIconSide,
	ToastPosition,
} from '@/helpers/vfm/types';
import { VFMHelper } from '@/helpers/vfm/vfm-helper';

const checkmarkCircleOutline =
	'data:image/svg+xml;utf8,' +
	encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
		<path d="M448 256c0-106-86-192-192-192S64 150 64 256s86 192 192 192 192-86 192-192z"
			fill="none" stroke="currentColor" stroke-width="32"/>
		<path fill="none" stroke="currentColor" stroke-width="32"
			stroke-linecap="round" stroke-linejoin="round"
			d="M352 176L217.6 336 160 272"/>
  		</svg>`);
const arrowUndo =
	'data:image/svg+xml;utf8,' +
	encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
		<path d="M448 440a16 16 0 01-12.61-6.15c-22.86-29.27-44.07-51.86-73.32-67C335 352.88 301 345.59 256 344.23V424a16 16 0 01-27 11.57l-176-168a16 16 0 010-23.14l176-168A16 16 0 01256 88v80.36c74.14 3.41 129.38 30.91 164.35 81.87C449.32 292.44 464 350.9 464 424a16 16 0 01-16 16z"
			fill="none" stroke="currentColor" stroke-width="32" stroke-linejoin="round"/>
		</svg>`);
const closeCircleOutline =
	'data:image/svg+xml;utf8,' +
	encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
		<path d="M448 256c0-106-86-192-192-192S64 150 64 256s86 192 192 192 192-86 192-192z" fill="none" stroke="currentColor" stroke-width="32" />
		<path fill="none" stroke="currentColor" stroke-width="32" stroke-linecap="round" stroke-linejoin="round" d="M320 320L192 192M320 192L192 320" />
		</svg>`);

const TOAST_DEFAULT_MESSAGE_TIMEOUT = 1500;

function toastTranslate(key: string, values?: Record<string, unknown>): string {
	const i18n = useNuxtApp().$i18n;
	return (values != null ? i18n.t(key, values) : i18n.t(key)) as string;
}

const LIST_TRANSLATABLES = () => {
	const t = toastTranslate;
	return {
		add: {
			[TitleListName.WATCHLIST]: t('WEBAPP_WATCHLIST_TOAST_ADDED'),
			[TitleListName.SEENLIST]: t('WEBAPP_SEENLIST_TOAST_SEEN'),
			[TitleListName.LIKELIST]: t('WEBAPP_LIKELIST_TOAST_ADDED'),
			[TitleListName.DISLIKELIST]: t('WEBAPP_DISLIKELIST_TOAST_ADDED'),
		},
		remove: {
			[TitleListName.WATCHLIST]: t('WEBAPP_WATCHLIST_TOAST_REMOVED'),
			[TitleListName.SEENLIST]: t('WEBAPP_SEENLIST_TOAST_UNSEEN'),
			[TitleListName.LIKELIST]: t('WEBAPP_LIKELIST_TOAST_REMOVED'),
			[TitleListName.DISLIKELIST]: t('WEBAPP_DISLIKELIST_TOAST_REMOVED'),
		},
	};
};

const showToast = async ({ message, duration, position, color, cssClass, buttons }: ShowToastPayload) => {
	await VFMHelper.ensureVfmCss();

	const { promise } = VFMHelper.open({
		type: 'toast',
		props: {
			escToClose: false,
			clickToClose: false,
			lockScroll: false,
			background: 'interactive',
			focusTrap: false,
			overlayBehavior: 'persist',
			overlayClass: 'vfm-toast-overlay',
		},
		slotComponent: ToastModal,
		slotProps: {
			message,
			duration,
			position,
			color,
			cssClass,
			buttons,
		},
	});

	return promise;
};

async function addToast({
	message,
	duration = TOAST_DEFAULT_MESSAGE_TIMEOUT,
	buttons = [],
	position = 'bottom',
	color = 'tertiary',
	cssClass = 'jw-toast',
	showCloseButton = true,
}: {
	message: string;
	duration?: number;
	buttons?: ToastButton[];
	position?: ToastPosition;
	color?: ToastColors;
	cssClass?: string;
	showCloseButton?: boolean;
}) {
	if (showCloseButton) {
		const closeButton: ToastButton = {
			text: toastTranslate('WEBAPP_CLOSE'),
			role: 'cancel',
			side: ToastIconSide.END,
		};
		buttons = [...buttons, closeButton];
	}

	return await showToast({
		message,
		duration,
		position,
		color,
		cssClass,
		buttons,
	});
}

function toastSuccess({
	message,
	action,
	duration = TOAST_DEFAULT_MESSAGE_TIMEOUT,
	position = 'bottom',
	closeButton = true,
}: {
	message: string;
	action?: ToastButton;
	duration?: number;
	position?: ToastPosition;
	closeButton?: boolean;
}) {
	const icon: ToastButton = {
		icon: checkmarkCircleOutline,
		side: ToastIconSide.START,
	};

	const buttons: ToastButton[] = [icon];
	if (action) {
		buttons.push(action);
		closeButton = false;
	}
	addToast({ message, duration, buttons, position, showCloseButton: closeButton });
}

function toastError({
	message,
	action,
	duration = TOAST_DEFAULT_MESSAGE_TIMEOUT,
	position = 'bottom',
	closeButton = true,
}: {
	message: string;
	action?: ToastButton;
	duration?: number;
	position?: ToastPosition;
	closeButton?: boolean;
}) {
	if (!import.meta.client) return;

	const icon: ToastButton = {
		icon: closeCircleOutline,
		side: ToastIconSide.START,
	};

	const buttons: ToastButton[] = [icon];
	if (action) {
		buttons.push(action);
		closeButton = false;
	}

	addToast({ message, duration, buttons, position, color: 'danger', showCloseButton: closeButton });
}

function showSuccessWithUndo({
	message,
	undoCallback,
	duration,
}: {
	message: string;
	undoCallback: ToastCallback;
	duration?: number;
}) {
	const { message: undoMessage, callback } = undoCallback;
	// Undo Action callback wrapper
	const undoActionCallback = (message: TranslateResult) => async () => {
		const icon: ToastButton = {
			icon: arrowUndo,
			side: ToastIconSide.START,
		};

		callback();

		addToast({ message: String(message), duration, buttons: [icon] });
	};

	// Undo Action for Toast
	const trUndoButtonText = toastTranslate('WEBAPP_UNDO_BUTTON');
	const action: ToastButton = createToastAction(trUndoButtonText, undoActionCallback(undoMessage));
	toastSuccess({ message, action, duration, closeButton: false });
}

function createToastAction(text: string, callback: Function): ToastButton {
	return {
		text,
		handler: () => {
			try {
				callback();
			} catch (err) {
				// error message
				console.error('[createToastAction] callback err: ', err);
			}
		},
	};
}

let translatable: Record<ToastAction, any> | null = null;
function getListTranslatable(listAction: ToastAction, listType: TitleListNames) {
	if (!translatable) {
		translatable = LIST_TRANSLATABLES();
	}
	return (translatable[listAction] as any)[listType] as string;
}

function getSeenToastMessage(
	isTitleInSeenlist: boolean,
	payload: {
		titleObjectType: ObjectType;
		titleName: string;
		showSeason?: number;
		showEpisode?: number;
	}
) {
	const trUnseenMessage = toastTranslate('WEBAPP_SEENLIST_TOAST_UNSEEN');
	const trSeenMessage = toastTranslate('WEBAPP_SEENLIST_TOAST_SEEN');
	const { titleName, showEpisode, showSeason } = payload;
	let messageTitle = titleName;

	if (showSeason) {
		messageTitle += ` S${showSeason}`;
	}

	if (showEpisode) {
		messageTitle += ` E${showSeason}`;
	}

	return !isTitleInSeenlist ? `${messageTitle} ${trSeenMessage}` : `${messageTitle} ${trUnseenMessage}`;
}

function addTitleToListToast(
	titleListName: TitleListNames,
	titleDetails: ListMutationTitleDetailParam,
	undoCallback?: Function
) {
	// exclude more lists for now

	if (titleListName !== TitleListName.MORELIST) {
		const trAddMessage = getListTranslatable('add', titleListName);
		const trRemoveMessage = getListTranslatable('remove', titleListName);
		const fullTitle = getFullTitle(titleListName, titleDetails);

		const undoAction = {
			message: `${fullTitle} ${trRemoveMessage}`,
			callback: () => undoCallback && undoCallback(),
		};
		showSuccessWithUndo({ message: `${fullTitle} ${trAddMessage}`, undoCallback: undoAction });
	}
}

function removeTitleFromListToast(titleListName: TitleListNames, titleDetails: ListMutationTitleDetailParam) {
	const fullTitle = getFullTitle(titleListName, titleDetails);
	// exclude more lists for now
	if (titleListName !== TitleListName.MORELIST) {
		const trRemoveMessage = getListTranslatable('remove', titleListName);
		toastSuccess({ message: `${fullTitle} ${trRemoveMessage}` });
	}
}

function getFullTitle(titleListName: TitleListNames, titleDetails: ListMutationTitleDetailParam) {
	if (titleListName === TitleListName.SEENLIST)
		return titleDetails.objectType === ObjectType.ShowEpisode &&
			titleDetails.seasonNumber &&
			titleDetails.episodeNumber
			? `${titleDetails.title} - ${toastTranslate('WEBAPP_SEASON_X_EPISODE_Y', {
					season: titleDetails.seasonNumber,
					episode: titleDetails.episodeNumber,
			  })}`
			: titleDetails.title;
	//@ts-expect-error
	return [TitleListName.LIKELIST, TitleListName.DISLIKELIST].includes(titleListName) && !!titleDetails.seasonTitle
		? `${titleDetails.title} - ${titleDetails.seasonTitle}`
		: titleDetails.title;
}

export {
	addTitleToListToast,
	addToast,
	createToastAction,
	getListTranslatable,
	getSeenToastMessage,
	removeTitleFromListToast,
	showSuccessWithUndo,
	toastError,
	toastSuccess,
};
